<script setup>
import ContentHeading from '@/pages/projects/partials/ContentHeading.vue'
import ContentTextEditor from '@/pages/projects/partials/ContentTextEditor.vue'
import ContentSwiper from '@/pages/projects/partials/ContentSwiper.vue'
import { index as projectIndex } from '@/actions/App/Http/Controllers/ProjectController'
import { ChevronLeftIcon } from '@heroicons/vue/24/solid'

const props = defineProps({
    project: {
        type: Object,
        required: true,
    },
})
</script>

<template>
    <Head :title="props.project.name">
        <meta
            name="description"
            :content="
                props.project.description ??
                `Projekt ${props.project.name} - realizace od firmy Kalianko. Podrobnosti a fotografie z realizace.`
            "
        />
    </Head>

    <section class="section-width mt-14 space-y-10 py-20 lg:space-y-32">
        <div>
            <Link
                :href="projectIndex()"
                class="inline-flex w-full items-center justify-center rounded-full border border-sky-500/30 bg-sky-500/10 px-6 py-3 text-base font-semibold text-sky-500 backdrop-blur-lg transition hover:bg-sky-500/20 sm:w-auto"
                aria-label="Nezávazná poptávka"
            >
                <ChevronLeftIcon class="mr-2 size-4" />
                Zpět na projekty
            </Link>
        </div>

        <div class="relative isolate overflow-hidden lg:overflow-visible">
            <div
                class="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:items-start lg:gap-y-10"
            >
                <div
                    class="lg:col-span-2 lg:col-start-1 lg:row-start-1 lg:mx-auto lg:grid lg:w-full lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8"
                >
                    <div class="lg:pr-4">
                        <div class="lg:max-w-lg">
                            <h1
                                class="mt-2 text-4xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl"
                            >
                                {{ project.name }}
                            </h1>
                        </div>
                    </div>
                </div>

                <div
                    class="lg:sticky lg:top-40 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:-mt-12 lg:-ml-12 lg:overflow-hidden"
                >
                    <img
                        class="w-full max-w-full rounded-xl object-cover shadow-xl select-none lg:h-auto lg:object-contain"
                        :src="project.thumbnail"
                        :alt="project.name"
                        loading="lazy"
                    />
                </div>

                <div
                    v-if="project.content"
                    class="lg:col-span-2 lg:col-start-1 lg:row-start-2 lg:mx-auto lg:grid lg:w-full lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8"
                >
                    <div class="lg:pr-4">
                        <div class="grid max-w-xl gap-y-10 text-base/7 text-gray-700 lg:max-w-lg">
                            <div
                                v-for="(content, index) in project.content"
                                :key="index"
                            >
                                <ContentHeading
                                    v-if="content.type === 'heading'"
                                    :content="content.data"
                                />

                                <ContentTextEditor
                                    v-else-if="content.type === 'texteditor'"
                                    :content="content.data"
                                />

                                <ContentSwiper
                                    v-else-if="content.type === 'swiper'"
                                    :content="content.data"
                                />

                                <div v-else>{{ content.type }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
